import BrandMark from "./BrandMark";
import React from "react";
import { Box, Button, Typography, Avatar } from "@mui/material";
import { Link, NavLink } from "react-router-dom";
import LogoutIcon from "@mui/icons-material/Logout";
import SidebarNav, { SidebarSectionLabel } from "./navigation/SidebarNav";
import useMenuCounts from "../hooks/useMenuCounts";
import {
  getGlobalNavigation,
  getPersonalNavigation,
  getNavigationForRole,
  getDesktopNavigation,
} from "../navigation/NavigationRegistry";
import { getPlatformRole } from "../navigation/navigationPermissions";

const ACCENT_MID = "#52B788";
const NAV_BG = "#FFFFFF";

// Phase 3 — Navigation Architecture (Step 7.5)
//
// LeftNav (the desktop SidebarNav shell) is now registry-driven: every menu
// item it renders comes from `NavigationRegistry`, filtered by the user's
// platform role / church role. No navigation menus are hard-coded here.
const GATHER_IDS = ["home", "churches", "prayer", "answeredPrayer", "myLists"];
const CIRCLES_SECTION_IDS = ["circles", "friends", "discover", "inbox"];
const PREFERENCES_IDS = ["settings", "platformAdmin", "domainInfrastructure", "foundingMemberCodes", "prayerTags", "docs"];

function pickItems(pool, ids) {
  return ids
    .map((id) => pool.find((item) => item.id === id))
    .filter(Boolean);
}

export default function LeftNav({ user, onLogout }) {
  const username = typeof user?.username === "string" ? user.username.trim() : "";
  const { unreadNotifications, unreadMessages } = useMenuCounts(user);
  const badgeCounts = {
    unreadNotifications,
    unreadMessages,
    unreadInbox: unreadNotifications + unreadMessages,
  };

  const platformRole = getPlatformRole(user);
  const navigablePersonalItems = getNavigationForRole(
    platformRole,
    getDesktopNavigation(getPersonalNavigation())
  );
  const navigablePool = [...getDesktopNavigation(getGlobalNavigation()), ...navigablePersonalItems];

  const gatherItems = pickItems(navigablePool, GATHER_IDS);
  const circlesItems = pickItems(navigablePool, CIRCLES_SECTION_IDS);
  const preferencesItems = pickItems(navigablePool, PREFERENCES_IDS);

  return (
    <Box
      component="nav"
      aria-label="Desktop navigation"
      sx={{
        width: 220,
        height: "100vh",
        backgroundColor: NAV_BG,
        borderRight: "1px solid #E8E2D9",
        display: "flex",
        flexDirection: "column",
        position: "sticky",
        top: 0,
        overflowY: "auto",
        scrollbarWidth: "none",
        "&::-webkit-scrollbar": { display: "none" },
      }}
    >
      {/* Logo */}
      <Box sx={{ px: 2.5, py: 2, pb: 2.5, borderBottom: "1px solid #E8E2D9" }}>
        <Link to="/" style={{ minHeight: 44, textDecoration: "none", display: "flex", alignItems: "center", gap: 8 }}>
          <BrandMark size={38} />
          <Box component="span" sx={{
            fontFamily: "'DM Serif Display', serif",
            fontSize: "17px",
            color: "#1A1714",
            lineHeight: 1,
            "& em": { color: "#2D6A4F", fontStyle: "italic" },
          }}>
            Life <em>Circle</em>
          </Box>
        </Link>
      </Box>

      {/* GATHER section */}
      <SidebarSectionLabel>Gather</SidebarSectionLabel>
      <SidebarNav items={gatherItems} badgeCounts={badgeCounts} />

      {/* MY CIRCLES section */}
      <SidebarSectionLabel>My Circles</SidebarSectionLabel>
      <SidebarNav items={circlesItems} badgeCounts={badgeCounts} />

      {/* PREFERENCES section */}
      <SidebarSectionLabel>Preferences</SidebarSectionLabel>
      <SidebarNav items={preferencesItems} badgeCounts={badgeCounts} />

      {/* Spacer */}
      <Box sx={{ flex: 1 }} />

      {/* User profile footer */}
      {user && (
        <Box sx={{
          px: 1.5,
          py: 1.5,
          borderTop: "1px solid #E8E2D9",
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
          gap: 0.5,
        }}>
          <Box
            component={NavLink}
            to="/profile"
            aria-label={username ? `${username} profile` : "Your profile"}
            sx={{
              minHeight: 56,
              minWidth: 0,
              p: 1,
              borderRadius: 1.5,
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              textDecoration: "none",
              "&:hover, &[aria-current='page']": { backgroundColor: "#E8F4EE" },
              "&:focus-visible": { outline: "3px solid #2D6A4F", outlineOffset: 2 },
            }}
          >
            <Avatar
              src={user.avatarUrl ?? undefined}
              alt=""
              sx={{ width: 32, height: 32, flexShrink: 0, border: `2px solid ${ACCENT_MID}` }}
            />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{
                fontSize: "13px", fontWeight: 600, lineHeight: 1.4, color: "#1A1714",
                fontFamily: "'DM Sans', sans-serif", overflowWrap: "anywhere",
              }}>
                {username || "Your account"}
              </Typography>
              <Typography sx={{ fontSize: "12px", color: "#6B6460", fontFamily: "'DM Sans', sans-serif" }}>
                View profile
              </Typography>
            </Box>
          </Box>
          <Button
            type="button"
            fullWidth
            aria-label="Log out"
            onClick={onLogout}
            startIcon={<LogoutIcon sx={{ fontSize: 18 }} />}
            sx={{
              px: 1, borderRadius: 1.5, minWidth: 44, minHeight: 44,
              justifyContent: "flex-start", color: "#6B6460", fontSize: "13px",
              "&:focus-visible": { outline: "3px solid #2D6A4F", outlineOffset: 2 },
            }}
          >
            Log out
          </Button>
        </Box>
      )}
    </Box>
  );
}
